<template>
    <v-container class="pa-5" fluid>
        <page-title>Rozliczenia czasu pracy / Raporty szczegółowe</page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading.approveReport"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row v-if="report && hasReports" align="center">
            <v-col cols="12" md="4">
                <v-row>
                    <v-col>
                        <v-card>
                            <v-card-text>
                                <table class="table table--simple">
                                    <tbody>
                                        <tr>
                                            <th>Pracownik:</th>
                                            <td>{{ report.user.firstName }} {{ report.user.lastName }}</td>
                                        </tr>
                                        <tr>
                                            <th>Łączny nakład:</th>
                                            <td>{{ report.reportedAmount }}</td>
                                        </tr>
                                        <tr>
                                            <th>Suma zadań:</th>
                                            <td>{{ report.finishedReports }}</td>
                                        </tr>
                                        <tr>
                                            <th>Ilość zaraportowanych zamówień:</th>
                                            <td>{{ report.finishedOrderReportsAmount }}</td>
                                        </tr>
                                        <tr>
                                            <th>Średnia ilość osób:</th>
                                            <td>{{ report.averageMachineStuffAmount }}</td>
                                        </tr>
                                        <tr>
                                            <th>Końcowy wynik wykonania planu:</th>
                                            <td>{{ convertToMinutes(report.planExecutionResult) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="auto">
                <v-col cols="12" md="auto" class="d-flex">
                    <v-icon class="mr-2" color="success">
                        mdi-timer-outline
                    </v-icon>
                    <span>
                        <strong>Start:</strong>
                        {{ formatDate(report.startDateTime) }}
                    </span>
                </v-col>
                <v-col cols="12" md="auto" class="d-flex">
                    <v-icon class="mr-2" color="error">
                        mdi-timer-off-outline
                    </v-icon>
                    <span>
                        <strong>Stop:</strong>
                        {{ formatDate(report.endDateTime) }}
                    </span>
                </v-col>
                <v-col cols="12" md="auto" class="d-flex">
                    <v-icon class="mr-2" color="info">
                        mdi-clock-outline
                    </v-icon>
                    <span>
                        <strong>Zaraportowano:</strong>
                        {{ parseDuration(report.duration) }}

                    </span>
                </v-col>
            </v-col>
            <v-col v-if="!hasOneUnfinishedReport" cols="auto">
                <canvas ref="chartContainer" style="width:350px;" />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="filteredHeaders"
                        :items="detailedReports"
                        :loading="detailedReportsLoading"
                        :mobile-breakpoint="0"
                        name="detailedReportsData"
                    >
                        <template #item.objectNumber="{item}">
                            {{ item.objectNumber }}
                        </template>
                        <template #item.userName="{item}">
                            {{ item.userName }}
                        </template>
                        <template #item.machineSymbol="{item}">
                            {{ item.machineSymbol }}
                        </template>
                        <template #item.processName="{item}">
                            {{ item.processName }}
                        </template>
                        <template #item.plateNumber="{item}">
                            {{ item.plateNumberRead }}
                        </template>
                        <template #item.reportAmount="{item}">
                            {{ parseEmptyValue(item.reportAmount) }}
                        </template>
                        <template #item.statusId="{item}">
                            {{ statusName(item.statusId) }}
                        </template>
                        <template #item.machineStuffAmount="{item}">
                            {{ item.machineStuffAmount }}
                        </template>
                        <template #item.startDate="{item}">
                            {{ formatDate(item.start) }}
                        </template>
                        <template #item.finishDate="{item}">
                            {{ formatDate(item.finish) }}
                        </template>
                        <template #item.duration="{item}">
                            {{ parseDuration(item.duration) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Szczegóły raportu"
                                            v-on="on"
                                            @click="openReport(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Szczegóły raportu</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
            <v-col>
                <v-row justify="end" dense>
                    <v-col cols="auto">
                        <v-btn
                            v-if="report"
                            color="success"
                            :loading="actionLoading.approveReport"
                            :disabled="report.statusId === 20"
                            data-sentry-label="Zatwierdzenie raportu"
                            @click="updateReportShiftSummary()"
                        >
                            Zatwierdź
                        </v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse.js'
import { errorHandler } from '@/utils/errorHandler'
import { Chart, ArcElement, DoughnutController, Legend } from 'chart.js'
import { sortBy } from 'lodash'
import { appConfig } from '@/config.js'


export default {
    name: 'DetailedReports',
    components: {
        PageTitle
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        parse,
        filters
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('reports/getReportShiftSummary', to.params.id),
            store.dispatch('reports/getDetailedReports', {
                reportShiftSummary_id: to.params.id
            })
        ])
            .then(response => {
                next(vm => {
                    vm.report = response[0]
                    vm.reportShiftSummaryId = to.params.id
                    setTimeout(() => {
                        vm.setDiagram()
                    }, 200)
                })
            })
            .catch(error => {
                errorHandler(error)
                next(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('reports/clearDetailedReportsFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            reportShiftSummaryId: null,
            chart: null,
            report: null,
            actionLoading: {
                approveReport: false
            },
            headers: [
                {
                    text: 'Numer',
                    value: 'objectNumber',
                    sortable: false,
                    show: true
                },
                {
                    text: 'Maszyna',
                    value: 'machineSymbol',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Proces',
                    value: 'processName',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Montaż',
                    value: 'plateNumber',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Nakład wykonany',
                    value: 'reportAmount',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Ilość osób',
                    value: 'machineStuffAmount',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Status',
                    value: 'statusId',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Data rozpoczęcia',
                    value: 'startDate',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Data zakończenia',
                    value: 'finishDate',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Czas trwania',
                    value: 'duration',
                    sortable: false,
                    align: 'center',
                    show: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100,
                    show: true
                }
            ]
        }
    },
    computed: {
        filteredHeaders() {
            return this.headers.filter(item => item.show)
        },
        detailedReports() {
            return this.$store.state.reports.reports.detailedReports
        },
        detailedReportsLoading() {
            return this.$store.state.reports.reports.detailedReportsLoading
        },
        statusNames() {
            return this.$store.state.reports.reports.statusNames
        },
        savedFilters() {
            return this.$store.state.reports.reports.detailedReportsFilters
        },
        diagramColors() {
            return appConfig.reports.diagramColors
        },
        hasOneUnfinishedReport() {
            return this.detailedReports.length === 1 && (this.detailedReports[0].statusId === 1 || this.detailedReports[0].statusId === 90) // rozpoczęty, przerwany
        },
        hasReports() {
            return this.detailedReports.length > 0
        },
        processes() {
            const processesHelper = {}

            const processes = this.detailedReports.reduce((processes, item) => {
                const key = `${item.processName}`

                if (!processesHelper[key]) {
                    processesHelper[key] = {
                        processCodeId: item.processCodeId,
                        processName: item.processName,
                        duration: item.duration,
                        diagramColor: item.uiActionColour
                    }

                    processes.push(processesHelper[key])
                } else {
                    processesHelper[key].duration += item.duration
                }

                return processes
            }, [])

            return sortBy(processes, 'processCodeId')
        }
    },
    mounted() {
        this.$store.dispatch('reports/getStatusNames')
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        getDetailedReports() {
            this.$store.dispatch('reports/getDetailedReports', {
                reportShiftSummary_id: this.reportShiftSummaryId,
                paginate: '',
                query: `?search=${this.filters.search}`
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openReport(report) {
            this.saveFiltersCleared()

            this.$router.push({
                name: 'reportingAndAnalysisReportsReportsDailyReportsShiftSummariesDetailedReportsReport',
                params: {
                    reportShiftSummaryId: this.reportShiftSummaryId,
                    id: report.id,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        updateReportShiftSummary() {
            this.actionLoading.approveReport = true

            this.$store.dispatch('reports/updateReportShiftSummary', {
                ...this.report,
                statusId: 20 //Zatwierdzony
            })
                .then(response => {
                    this.report = response
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.approveReport = false
                })
        },
        setDiagram() {
            if (this.report) {
                if (this.hasReports && !this.hasOneUnfinishedReport) {
                    Chart.register(ArcElement, DoughnutController, Legend)

                    const colors = this.processes.map(process => process.diagramColor)
                    const chartContainer = this.$refs.chartContainer

                    this.chart = new Chart(chartContainer, {
                        type: 'doughnut',
                        data: {
                            labels: this.processes.map(item => `${item.processName} (${this.parseDuration(item.duration)})`),
                            datasets: [{
                                label: 'Zadania',
                                data: this.processes.map(process => process.duration),
                                backgroundColor: colors ? colors : this.diagramColors,
                            }]
                        },
                        options: {
                            plugins: {
                                legend: {
                                    display: true,
                                    position: 'bottom'
                                }
                            },
                        }
                    })
                }
            } else {
                setTimeout(() => {
                    this.report = {
                        startDateTime: null,
                        endDateTime: null,
                        duration: null,
                        user: {
                            firstName: null,
                            lastName: null,
                        },
                        detailedReports: []
                    }

                    if (this.chart) {
                        this.chart.destroy()
                    }
                }, 500)
            }
        },
        statusName(statusId) {
            return this.statusNames ? this.statusNames.find(item => item.id == statusId).name : statusId
        },
        close() {
            this.$router.push({ name: 'reportingAndAnalysisReportsReportsDailyReportsShiftSummaries' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        changeFilter() {
            this.$store.dispatch('reports/setDetailedReportsFilters', this.filters)
                .catch(error => {
                    errorHandler(error)
                })
            this.getDetailedReports()
        }
    },
}

</script>